/**
 * @fileoverview Hook de Form Layer para gestión de campos dinámicos y formularios en TemplateMessagePreview.
 * Maneja únicamente lógica de formulario, validación y observación de campos sin lógica de negocio o UI.
 * Optimizado para prevenir re-renders innecesarios mediante observación selectiva.
 *
 * @module useTemplateMessageForm
 * @category Hook - Form Layer
 * @since 1.0.0
 * @version 1.1.0 - Separación de Form Layer y optimización de observación
 */
import { useMemo, useCallback } from 'react';
import { useWatch } from 'react-hook-form';

const useTemplateMessageForm = (
  control,
  setValue,
  dynamicFieldNames,
  carouselData
) => {
  /**
   * Observa valores de campos dinámicos principales en tiempo real.
   */
  const dynamicFieldValues = useWatch({
    control,
    name: dynamicFieldNames,
    defaultValue: dynamicFieldNames.map(() => '')
  });

  /**
   * Observa valores de campos del carousel en tiempo real.
   */
  const carouselFieldValues = useWatch({
    control,
    name: carouselData?.carouselFieldNames || [],
    defaultValue: []
  });

  /**
   * Observa archivo adjunto principal del mensaje.
   */
  const attachedFile = useWatch({
    control,
    name: 'attachedFile',
    defaultValue: []
  });

  /**
   * Observa archivos específicos de cada card del carousel.
   */
  const carouselFiles = useWatch({
    control,
    name: carouselData?.cards?.map((_, index) => `carouselFile_${index}`) || [],
    defaultValue: []
  });

  /**
   * Crea mapa de valores del carousel indexado por fieldName para acceso eficiente.
   */
  const carouselValuesMap = useMemo(() => {
    const valuesMap = {};
    (carouselData?.carouselFieldNames || []).forEach((fieldName, index) => {
      const values = Array.isArray(carouselFieldValues) ? carouselFieldValues : [carouselFieldValues];
      valuesMap[fieldName] = values[index] || '';
    });
    return valuesMap;
  }, [carouselData?.carouselFieldNames, carouselFieldValues]);

  /**
   * Función para actualizar mensaje en el formulario de manera optimizada.
   */
  const updateFormMessage = useCallback((message) => {
    setValue('message', message, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: false
    });
  }, [setValue]);

  /**
   * Determina si el formulario tiene cambios sin guardar.
   */
  const isFormDirty = useMemo(() => {
    const hasFieldValues = dynamicFieldValues.some((value) => value && value.toString().trim() !== '');
    const hasFiles = attachedFile?.length > 0 || carouselFiles?.some((f) => f?.length > 0);
    return hasFieldValues || hasFiles;
  }, [dynamicFieldValues, attachedFile, carouselFiles]);

  /**
   * Retorna el estado completo del formulario de manera optimizada.
   */
  return useMemo(() => ({
    dynamicFieldValues,
    carouselFieldValues,
    attachedFile,
    carouselFiles,
    carouselValuesMap,
    updateFormMessage,
    isFormDirty
  }), [
    dynamicFieldValues,
    carouselFieldValues,
    attachedFile,
    carouselFiles,
    carouselValuesMap,
    updateFormMessage,
    isFormDirty
  ]);
};

export default useTemplateMessageForm;
